import React, { useState, useCallback, useEffect } from "react";
import { useFormik } from "formik";
import { Button, Card, Col, Form, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { BsBoxArrowUpRight } from "react-icons/bs";
import * as Yup from "yup";
import { toast } from "react-toastify";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import ButtonImage from "../../../components/UI/Button/ButtonImage";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import PaginatedView from "../../../components/UI/PaginatedView/PaginatedView";
import TextAreaInputFormik from "../../../components/UI/TextInput/TextAreaInputFormik";
import htmlParaTextoPlano from "../../../components/Formatters/HtmlParaTextoPlano";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import FileDownloader from "../../../components/UI/Button/FileDownloader";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import ListaDemonstrativos from "./ListaDemonstrativos";
import useAtualizaDadosOs from "./hooks/useAtualizaDadosOS";
import ConsultaOcorrenciaDetalhes from "../../Parametrizacao/Ocorrencia/ConsultaOcorrenciaDetalhes";
import useVerificaResultado from "./hooks/useVerificaResultado";
import useSomenteConsultaOs from "../hooks/useSomenteConsultaOs";

const mostrarDetalhe = { exibir: () => { } };

const exibirDetalhe = (row) => {
  mostrarDetalhe.exibir({ row });
};

const columns = [
  {
    name: "N° Tipo de Ocorrência",
    selector: (row) => row.sqTipoOcorrencia,
    maxWidth: "15%",
    center: true,
    sortable: true,
  },
  {
    name: "Tipo de Ocorrência",
    selector: "dsTipoOcorrencia",
    maxWidth: "70%",
    cell: (row) => (
      <div>
        {htmlParaTextoPlano(row.txTipoOcorrencia)}
      </div>
    ),
  },
  {
    name: "Detalhe",
    selector: (row) => row.sqTipoOcorrencia,
    cell: (row) => (
      <ButtonImage
        className="btn-lg text-success pa-0 p-0"
        buttonImage={<BsBoxArrowUpRight />}
        tooltipImage={`Ver detalhes da ocorrência Nº ${row.sqTipoOcorrencia}`}
        onClick={() => exibirDetalhe(row)}
      />
    ),
    maxWidth: "15%",
    center: true,
    sortable: true,
  },
];

const validationSchema = Yup.object().shape({
  sqAcaoFiscal: Yup.string().default(),
  sqItemOcorrencia: Yup.string().default(),
  sqTipoOcorrencia: Yup.string().default(),
  dtPeriodoInicial: Yup.date(),
  dtPeriodoFinal: Yup.date(),
  dsOcorrencia: Yup.string()
    .default("")
    .test(
      "ds-ocorrencia",
      "Informe a descrição da ocorrência",
      (value) => (value || "").trim().length > 0,
    ),
  palavraChave: Yup.string().default(""),
  txPenalidade: Yup.string().default(""),
  txCompPenalidade: Yup.string().default(""),
  sequencialStatus: Yup.string().default(""),
  tipoDetalhamento: Yup.string().default(""),
  txInfringencia: Yup.string().default(""),
  txCompInfringencia: Yup.string().default(""),
  texto: Yup.string().default(""),
  dsTipoOcorrencia: Yup.string().default(""),
  fgExtrato: Yup.string().default(""),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (key === "dsOcorrencia") {
    initialValues[key] = "-";
  } else {
    initialValues[key] = "";
  }
});

const FormOcorrenciasAF = ({ history, dadosOs, dadosOcorrencia, onCancelar, tipoTela }) => {
  const [itemOcorrencia, setItemOcorrencia] = useState({});
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();
  const { request: requestOcorrencia, data: dataOcorrencia } = useApiClient();
  const [data, setData] = useState({ escondido: true, mostrarocorrencias: false });
  const [showDetalhe, setShowDetalhe] = useState({
    show: false,
    tipoOcorrencia: 0,
  });

  const { verificaResultado, existe } = useVerificaResultado({ sqAcaoFiscal: dadosOs.acaoFiscal });

  const somenteConsulta = useSomenteConsultaOs();

  useEffect(() => {
    if (dadosOs.acaoFiscal !== null && dadosOs.acaoFiscal !== "") {
      verificaResultado();
    }
  }, [dadosOs.acaoFiscal, verificaResultado]);

  const handleSubmit = useCallback(
    (values) => {
      const valoresTextoPlano = {
        ...values,
        dsOcorrencia: htmlParaTextoPlano(values.dsOcorrencia),
        txCompInfringencia: htmlParaTextoPlano(values.txCompInfringencia),
        txCompPenalidade: htmlParaTextoPlano(values.txCompPenalidade),
      };

      if (values.sqItemOcorrencia > 0) {
        requestForm(apiRoutes.fiscalizacao.ocorrencia.alterarOcorrencia, valoresTextoPlano);
      } else {
        requestForm(apiRoutes.fiscalizacao.ocorrencia.inserirOcorrencia, valoresTextoPlano);
      }
    },
    [requestForm],
  );

  const formik = useFormik({ initialValues, validationSchema, onSubmit: handleSubmit });

  const { setSubmitting, setErrors, setFieldValue, setValues, errors } = formik;

  const { carregarOcorrencia, ocorrencia } = useAtualizaDadosOs({ dadosOs, formik });

  const handleReset = () => {
    setFieldValue("dsOcorrencia", "");
  };

  useEffect(() => {
    if (!isLoading) setSubmitting(false);

    if (errorForm) {
      setErrors({ ...errors, ...errorForm });
      setSubmitting(false);
    }

    if (dataForm) {
      toast.success(dataForm.result);
      setErrors({ ...errors, ...errorForm });
      carregarOcorrencia(dadosOs.sequencial);
    }
  }, [setSubmitting, setErrors, errors, dataForm, errorForm, isLoading, carregarOcorrencia, dadosOs.sequencial]);

  useEffect(() => {
    if (ocorrencia.length > 0) {
      setItemOcorrencia(ocorrencia[0]);
      setData({
        escondido: false,
        mostrarocorrencias: true,
      });
    }
  }, [ocorrencia]);

  useEffect(() => {
    if (dadosOcorrencia?.sqItemOcorrencia > 0) {
      requestOcorrencia(apiRoutes.fiscalizacao.ocorrencia.consultarOcorrencia, {
        sqAcaoFiscal: dadosOs.acaoFiscal,
        sqItemOcorrencia: dadosOcorrencia.sqItemOcorrencia,
      });
    }
  }, [dadosOcorrencia, requestOcorrencia, dadosOs]);

  useEffect(() => {
    if (dataOcorrencia && dataOcorrencia.result) {
      setData({
        escondido: false,
        mostrarocorrencias: true,
      });

      const newValues = { ...dataOcorrencia.result[0] };

      newValues.dsOcorrencia = htmlParaTextoPlano(newValues.dsOcorrencia);
      newValues.txCompInfringencia = htmlParaTextoPlano(newValues.txCompInfringencia);
      newValues.txCompPenalidade = htmlParaTextoPlano(newValues.txCompPenalidade);

      setValues((curr) => ({ ...curr, ...newValues }));
      setItemOcorrencia(newValues);
    }
  }, [dataOcorrencia, setValues]);

  const handleClickOcorrencia = useCallback(
    (row) => {
      setData({
        escondido: false,
        mostrarocorrencias: true,
      });

      setFieldValue("sqAcaoFiscal", dadosOs.acaoFiscal);
      setFieldValue("sqTipoOcorrencia", row.sqTipoOcorrencia);
      setFieldValue("sqItemOcorrencia", 0);
      setFieldValue(
        "dtPeriodoInicial",
        row.dtPeriodoInicial != null ? row.dtPeriodoInicial : dadosOs.dataInicialFiscalizacao,
      );
      setFieldValue("dtPeriodoFinal", row.dtPeriodoFinal != null ? row.dtPeriodoFinal : dadosOs.dataFinalFiscalizacao);
      setFieldValue("txInfringencia", row.txInfringencia);
      setFieldValue("txCompInfringencia", htmlParaTextoPlano(row.txCompInfringencia));
      setFieldValue("txPenalidade", row.txPenalidade);
      setFieldValue("txCompPenalidade", htmlParaTextoPlano(row.txCompPenalidade));
      setFieldValue("dsTipoOcorrencia", row.dsTipoOcorrencia);
      setFieldValue("tipoDetalhamento", row.tipoDetalhamento);
      setFieldValue("fgExtrato", row.fgExtrato);
      setFieldValue("dsOcorrencia", "");
      setFieldValue("sqModeloImportacao", row.sqModeloImportacao);
      setFieldValue("arquivoModelo", row.arquivoModelo);
      setFieldValue("nomeArquivo", row.nomeArquivo);
      setFieldValue("dsModeloImportacao", row.dsModeloImportacao);
    },

    [setFieldValue, dadosOs],
  );

  mostrarDetalhe.exibir = (dados) => {
    setData({ escondido: true, mostrarocorrencias: true });
    setShowDetalhe({
      show: true,
      tipoOcorrencia: dados.row.sqTipoOcorrencia,
    });
  };

  const handleVoltar = () => {
    setData({ escondido: true, mostrarocorrencias: false });
    setShowDetalhe({
      show: false,
      tipoOcorrencia: 0,
    });
  };

  return (
    <>
      {showDetalhe.show && (
        <ConsultaOcorrenciaDetalhes
          location={history.location}
          sqTipoOcorrencia={showDetalhe.tipoOcorrencia}
          history={history}
          handleVoltar={handleVoltar}
        />
      )}
      {
        <div hidden={data.mostrarocorrencias}>
          <PaginatedView
            title="Tipos de Ocorrência"
            keyField="sqTipoOcorrencia"
            descriptionField="sqTipoOcorrencia"
            defaultSearch
            defaultSearchPlaceholder="Digite o Nº da Ocorrência"
            queryService={apiRoutes.fiscalizacao.ocorrencia.listaTipoOcorrencia}
            columns={columns}
            advancedSearchFormik={formik}
            advancedSearchFormikInitialValues={initialValues}
            onRowClicked={(row) => handleClickOcorrencia(row)}
            history={history}
            reduxKey="tipoOcorrenciaOs"
          >
            <Row>
              <Form.Group as={Col} sm={12} md={3}>
                <Form.Label>Descrição</Form.Label>
                <TextInputFormik formik={formik} field="texto" />
              </Form.Group>
              <Form.Group as={Col} sm={12} md={3}>
                <Form.Label>Palavra-chave</Form.Label>
                <TextInputFormik formik={formik} field="palavraChave" />
              </Form.Group>
              <Form.Group as={Col} sm={12} md={3}>
                <Form.Label>Penalidade</Form.Label>
                <TextInputFormik formik={formik} field="txPenalidade" />
              </Form.Group>
            </Row>
          </PaginatedView>
        </div>
      }

      <span id="dadosInclusaoOcorrencia">
        <div hidden={data.escondido && showDetalhe} className="card shadow">
          <div className="card-body">
            <FormAlert formik={formik} data={dataForm} errors={errorForm} isLoading={isLoading} />
            <Form noValidate onSubmit={formik.handleSubmit}>
              <Tabs defaultActiveKey="geral" transition={false} id="tabOcor">
                <Tab className="px-3" eventKey="geral" title={<span>&nbsp;Dados Gerais</span>}>
                  <Row className="mt-4">
                    <Col sm="2" lg="2">
                      <Card.Title as="strong">N° Tipo de Ocorrência</Card.Title>
                    </Col>
                    <Col sm="2" lg="2">
                      <Card.Title as="strong">Tipo de Ocorrência</Card.Title>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="2" lg="2">
                      <Card.Text as="p">{formik.values.sqTipoOcorrencia}</Card.Text>
                    </Col>
                    <Col sm="10" lg="10">
                      <Card.Text as="p">{htmlParaTextoPlano(formik.values.dsTipoOcorrencia)}</Card.Text>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col sm="2" lg="2">
                      <Card.Title as="strong">Infringência</Card.Title>
                    </Col>
                    <Col sm="6" lg="6">
                      <Card.Title as="strong">Penalidade</Card.Title>
                    </Col>
                    <Col sm="4" lg="4">
                      <Card.Title as="strong">Modelo de Importação</Card.Title>
                    </Col>
                  </Row>
                  <Row className="mb-5">
                    <Col sm="2" lg="2">
                      <Card.Text as="p">{htmlParaTextoPlano(formik.values.txInfringencia)}</Card.Text>
                    </Col>
                    <Col sm="6" lg="6">
                      <Card.Text as="p">{htmlParaTextoPlano(formik.values.txPenalidade)}</Card.Text>
                    </Col>
                    <Col sm="4" lg="4">
                      <Card.Text as="p">
                        {formik.values.sqModeloImportacao === 0 ? (
                          <p>[Não possui]</p>
                        ) : (
                          <FileDownloader
                            binario={formik.values.arquivoModelo}
                            nomeArquivo={formik.values.nomeArquivo}
                            tooltip={formik.values.dsModeloImportacao}
                          />
                        )}
                      </Card.Text>
                    </Col>
                  </Row>

                  <Row>
                    <Form.Group className="mb-3" as={Col} sm={12} md={12}>
                      <span>Descrição</span>
                      <TextAreaInputFormik formik={formik} field="dsOcorrencia" rows="6" disabled={somenteConsulta} />
                    </Form.Group>
                  </Row>

                  <Row>
                    <Form.Group className="form-group-periodos mb-3" as={Col} sm={12} md={6}>
                      <Form.Label>Período de Referência</Form.Label>
                      <div className="form-inline">
                        <DateTimeInputFormik formik={formik} field="dtPeriodoInicial" disabled={somenteConsulta} />
                        <Form.Label className="mx-3"> a </Form.Label>
                        <DateTimeInputFormik formik={formik} field="dtPeriodoFinal" disabled={somenteConsulta} />
                      </div>
                    </Form.Group>
                  </Row>

                  <Row>
                    <Form.Group className="mb-3" as={Col} sm={12} md={12}>
                      <span>Complemento de Infringência</span>
                      <TextAreaInputFormik
                        formik={formik}
                        field="txCompInfringencia"
                        rows="6"
                        disabled={somenteConsulta}
                      />
                    </Form.Group>
                  </Row>

                  <Row>
                    <Form.Group className="mb-3" as={Col} sm={12} md={12}>
                      <span>Complemento de Penalidade</span>
                      <TextAreaInputFormik
                        formik={formik}
                        field="txCompPenalidade"
                        rows="6"
                        disabled={somenteConsulta}
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col>
                      {formik.isSubmitting ? (
                        <span>
                          <Spinner animation="border" role="status" /> Salvando Ocorrência...
                        </span>
                      ) : (
                        !somenteConsulta && (
                          <Button
                            variant="primary"
                            className="mr-3"
                            size="md"
                            onClick={formik.handleSubmit}
                            disabled={existe === "S"}
                          >
                            Salvar
                          </Button>
                        )
                      )}
                      {"    "}
                      {!formik.isSubmitting && !somenteConsulta && (
                        <Button
                          variant="light"
                          className="mr-3"
                          size="md"
                          onClick={() => handleReset()}
                          disabled={existe === "S"}
                        >
                          Limpar
                        </Button>
                      )}
                      {"    "}
                      {!formik.isSubmitting &&
                        (tipoTela === "I" ? (
                          <Button
                            variant="danger"
                            size="md"
                            onClick={() => setData({ escondido: true, mostrarocorrencias: false })}
                            disabled={existe === "S"}
                          >
                            Cancelar
                          </Button>
                        ) : (
                          <Button variant="danger" size="md" onClick={onCancelar} disabled={existe === "S"}>
                            Cancelar
                          </Button>
                        ))}
                    </Col>
                  </Row>
                </Tab>
                {formik.values.sqItemOcorrencia > 0 && (
                  <Tab className="px-3 py-5" eventKey="demonstrativo" title={<span>&nbsp;Demonstrativo</span>}>
                    <ListaDemonstrativos
                      dadosOs={dadosOs}
                      dadosOcorrencia={itemOcorrencia}
                      history={history}
                      tipoTela={tipoTela}
                    />
                  </Tab>
                )}
              </Tabs>
            </Form>
          </div>
        </div>
      </span>
    </>
  );
};

export default FormOcorrenciasAF;
